import { useMemo, useState } from 'react';
import { useTable, useSortBy, Row } from 'react-table';
import { Icons } from '../icons/icons';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { StaticTableRow } from './static-table-row';
import { DraggableTableRow } from './draggle-table-row';

type Column = {
  accessor: string;
  Header: string;
  sort?: boolean;
  Cell?: any;
};

type Props = {
  isLoading?: boolean;
  columns: Column[];
  rows: { [key: string]: string | number }[];
  onUpdate?(item: any): void;
  onDelete?(item: any): void;
  onExport?(item: any): void;
  onInspect?(item:any): void;
  onDragEnd(id: string, item: any): void;
};

// TODO: update this table to use new implementation like paginatedTable

export const SortableTable = (props: Props): JSX.Element => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const data = useMemo(() => props.rows, [props.rows]);
  const columns = useMemo(() => props.columns, [props.columns]);
  const items = useMemo(() => data?.map(({ id }) => id), [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data }, useSortBy);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  function handleDragStart(event: { active: { id: string } }): void {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = over ? items.indexOf(over.id) : -1;
      props.onDragEnd(active.id, arrayMove(data, oldIndex, newIndex));
    }
    setActiveId(null);
  }

  function handleDragCancel(): void {
    setActiveId(null);
  }

  const selectedRow = useMemo(() => {
    if (!activeId) {
      return null;
    }
    const row = rows.find(({ original }) => original.id === activeId);
    if (row) {
      prepareRow(row);
    }
    return row;
  }, [activeId, rows, prepareRow]);

  return (
    <div className="table-container">
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
      >
        <table {...getTableProps()} className="table sortable-table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => {
                  const props = column.sort
                    ? column.getHeaderProps(column.getSortByToggleProps())
                    : column.getHeaderProps();
                  return (
                    <th {...props} className="header-cell">
                      {column.render('Header')}
                      {column.sort && (
                        <span className="sort-arrows">
                          {!column.isSorted ? <Icons.SortArrows /> : column.isSortedDesc ? <Icons.SortArrowUp /> : <Icons.SortArrowDown />}
                        </span>
                      )}
                    </th>
                  );
                })}
                {(typeof props.onDelete === 'function' || typeof props.onUpdate === 'function') && (
                  <th></th>
                )}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
          <SortableContext items={items as any} strategy={verticalListSortingStrategy}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <DraggableTableRow
                  key={row.original.id}
                  row={row}
                  onDelete={props.onDelete ? (item: Row): void => props.onDelete?.(item) : undefined}
                  onUpdate={props.onUpdate ? (item: Row): void => props.onUpdate?.(item) : undefined}
                  onExport={props.onExport ? (item: Row): void => props.onExport?.(item) : undefined}
                  onInspect={props.onInspect ? (item: Row): void => props.onInspect?.(item) : undefined}
                />
              );
            })}
            </SortableContext>
          </tbody>
        </table>
        {props.isLoading && <div className='text-center no-data-container'>
          <div><Icons.Data /></div>
          <p className="no-data-table">Loading...</p>
        </div>}
        {!props.isLoading && !rows?.length && <div className='text-center no-data-container'>
          <div><Icons.Data /></div>
          <p className="no-data-table">No data available in table.</p>
        </div>}
        <DragOverlay>
        {activeId && (
          <table style={{ width: '100%' }}>
            <tbody>
              <StaticTableRow { ...selectedRow } />
            </tbody>
          </table>
        )}
      </DragOverlay>
      </DndContext>
    </div>
  );
};
